/**
 * The renderer half of the hang-event context (main half:
 * electron/analytics/rendererUiContext.ts). A frozen renderer cannot say what
 * it was doing, so it tells main AHEAD of time: this module derives a tiny,
 * low-cardinality snapshot of the UI and throttles how often it crosses IPC.
 * `useHangContextPublisher` wires it to the stores.
 */

import type { Tab } from '../../features/workspace/core/types/workspaceTypes';
import { DATABASE_ENGINE_IDS, type DatabaseEngineId } from '../engines/engineTypes';
import type { RendererUiContext } from './analyticsTypes';
import { bucketCount } from './telemetryBuckets';

/** At most one publish per interval; a change inside it goes out at its end. */
export const UI_CONTEXT_PUBLISH_INTERVAL_MS = 500;

/**
 * `activeTabType` is typed by the SHARED vocabulary (`UI_CONTEXT_TAB_TYPES`,
 * which main validates against): a new `Tab['type']` missing from it makes
 * `buildUiContextSnapshot` fail to compile. The reverse direction (a stale
 * entry) is pinned in uiContextPublisher.test.ts.
 */
export type UiContextSnapshot = RendererUiContext;

type ConnectionLike = { readonly id: string; readonly engine?: string };

export type UiContextSources = {
  readonly tabs: readonly Pick<Tab, 'id' | 'type' | 'connectionInfo'>[];
  readonly activeTabId: string | null;
  /** Live connections first: they also cover ones never saved (e.g. the embed demo). */
  readonly activeConnections: readonly { readonly id: string; readonly connection: ConnectionLike }[];
  readonly connections: readonly ConnectionLike[];
  readonly restoreInProgress: boolean;
};

const asEngineId = (engine: string): DatabaseEngineId | 'none' =>
  (DATABASE_ENGINE_IDS as readonly string[]).includes(engine) ? (engine as DatabaseEngineId) : 'none';

/** The active tab's connection engine, from the live connection or its saved definition. */
function activeEngineOf(sources: UiContextSources, connectionId: string | undefined): DatabaseEngineId | 'none' {
  if (!connectionId) return 'none';
  const connection =
    sources.activeConnections.find((active) => active.id === connectionId)?.connection ??
    sources.connections.find((saved) => saved.id === connectionId);
  if (!connection) return 'none';
  // `Connection.engine` is documented as defaulting to mssql when omitted.
  return asEngineId(connection.engine ?? 'mssql');
}

export function buildUiContextSnapshot(sources: UiContextSources): UiContextSnapshot {
  const activeTab = sources.tabs.find((tab) => tab.id === sources.activeTabId);
  return {
    tabsBucket: bucketCount(sources.tabs.length),
    activeTabType: activeTab?.type ?? 'none',
    activeEngine: activeEngineOf(sources, activeTab?.connectionInfo?.connectionId),
    restoreInProgress: sources.restoreInProgress,
  };
}

const sameSnapshot = (a: UiContextSnapshot | null, b: UiContextSnapshot): boolean =>
  a !== null &&
  a.tabsBucket === b.tabsBucket &&
  a.activeTabType === b.activeTabType &&
  a.activeEngine === b.activeEngine &&
  a.restoreInProgress === b.restoreInProgress;

export type UiContextPublisher = {
  /** Offer the current snapshot; sent now, at the end of the interval, or not at all if unchanged. */
  publish: (snapshot: UiContextSnapshot) => void;
  /** Cancel a pending trailing send. */
  dispose: () => void;
};

/**
 * Leading + trailing throttle with change detection. Leading, not a pure
 * debounce: the moment that matters most is the one right before a hang, and
 * a change followed by a freeze inside a debounce window would never reach
 * main. Unchanged snapshots (every keystroke updates the workspace store)
 * send nothing.
 *
 * One exception to the throttle: `restoreInProgress` turning ON is sent at
 * once, carrying whatever else was waiting. A renderer that freezes right
 * after a restore starts is exactly the case this context exists for; a
 * throttled `true` would reach main only after the freeze. Turning OFF, like
 * every other change, waits for the interval.
 */
export function createUiContextPublisher(
  send: (snapshot: UiContextSnapshot) => void,
  intervalMs: number = UI_CONTEXT_PUBLISH_INTERVAL_MS,
  now: () => number = () => Date.now(),
): UiContextPublisher {
  let lastSent: UiContextSnapshot | null = null;
  let lastSentAt = Number.NEGATIVE_INFINITY;
  let pending: UiContextSnapshot | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const sendNow = (snapshot: UiContextSnapshot): void => {
    lastSent = snapshot;
    lastSentAt = now();
    send(snapshot);
  };

  const flushPending = (): void => {
    timer = null;
    const snapshot = pending;
    pending = null;
    if (snapshot && !sameSnapshot(lastSent, snapshot)) sendNow(snapshot);
  };

  const cancelPending = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    pending = null;
  };

  return {
    publish: (snapshot) => {
      if (snapshot.restoreInProgress && !(lastSent?.restoreInProgress ?? false)) {
        cancelPending();
        sendNow(snapshot);
        return;
      }
      if (timer !== null) {
        pending = snapshot;
        return;
      }
      if (sameSnapshot(lastSent, snapshot)) return;
      const wait = lastSentAt + intervalMs - now();
      if (wait <= 0) {
        sendNow(snapshot);
        return;
      }
      pending = snapshot;
      timer = setTimeout(flushPending, wait);
    },
    dispose: cancelPending,
  };
}
